import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { currentBranding } from '../../../config/branding';
import { branding, connectorStatus } from '../../../config/enum';
import { useAppDispatch } from '../../../redux/hooks';
import { updateDeviceFilters } from '../../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import dashboard from '../../../utils/api/dashboard';

type PieChartDataType = {
  name: string;
  value: number;
  color: string;
};

/**
 * Connector Status
 * @returns
 */
export default function ConnectorStatusChart() {
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [pieChartData, setPieChartData] = useState<PieChartDataType[]>([
    { name: 'Charging', value: 0, color: '#0099D6' },
    { name: 'Available', value: 0, color: '#00A79D' },
    { name: 'Preparing', value: 0, color: '#020cc6ff' },
    { name: 'Unavailable', value: 0, color: '#6C757D' },
    { name: 'Finishing', value: 0, color: '#F79009' },
    { name: 'Faulted', value: 0, color: '#F04438' },
    { name: 'Others', value: 0, color: '#C0C0C0' },
  ]);

  /**
   * Function to handle redirect device management page with filter
   */
  const handleClickPieChartCell = (key: string) => {
    if (currentBranding === branding.VOLTIE) {
      let valueForFilter: string[] = [key];
      if (key === 'Others') {
        valueForFilter = [
          connectorStatus.PREPARING,
          connectorStatus.RESERVED,
          connectorStatus.UNAVAILABLE,
        ];
      }
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: valueForFilter,
          type: 'restAndAdd',
        }),
      );
      navigate('/ev-admin/device-management');
    }
  };

  /**
   * Function to fetch connector status report
   */
  const fetchConnectorStatusReport = () => {
    dashboard
      .getConnectorStatusReport()
      .then((response) => {
        const result = response.data;

        setPieChartData((previousData: any) =>
          previousData.map((item: any) => {
            const found = result.find(
              (r: any) => r.status.toLowerCase() === item.name.toLowerCase(),
            );
            return {
              ...item,
              value: found ? found.count : 0,
            };
          }),
        );
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to show custom tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="connector-status-custom-tooltip">
          <p className="label" style={{ color: data?.payload?.color }}>
            {data?.name
              ? `${t(
                  (data.name.charAt(0).toLowerCase() + data.name.slice(1)) as any,
                )} : ${data.value}`
              : ''}
          </p>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    fetchConnectorStatusReport();
  }, []);

  return (
    <div className="connector-status-chart-wrap">
      <div className="connector-status-chart-header">
        <div className="header-title">
          <h6>{t('connectorStatus')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/device-management')}>
            <NavigateArrow />
          </span>
        </div>
      </div>
      <div className="connector-status-chart-container">
        <PieChart width={250} height={250}>
          <Pie
            data={pieChartData}
            cx={125}
            cy={125}
            innerRadius={60}
            outerRadius={100}
            fill="#8884d8"
            paddingAngle={2}
            dataKey="value">
            {pieChartData.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.color}
                style={{ outline: 'none', cursor: 'pointer' }}
                onClick={() => handleClickPieChartCell(entry.name)}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
        <div className="total-sessions-wrap">
          <p>
            {t('total')} <br /> {t('connectors')}
          </p>
          <span>{pieChartData.reduce((sum, item) => sum + item.value, 0)}</span>
        </div>
      </div>
      <div className="pie-chart-label">
        {pieChartData.map((eachData) => (
          <div className="each-label">
            <div
              className="each-label-color"
              style={{ '--label-color': eachData.color } as React.CSSProperties}
            />
            <p
              role="presentation"
              onClick={() => handleClickPieChartCell(eachData.name)}
              style={currentBranding === branding.VOLTIE ? { cursor: 'pointer' } : undefined}>
              {t((eachData.name.charAt(0).toLowerCase() + eachData.name.slice(1)) as any)}
            </p>
            <div className="each-label-value">
              <span>{eachData.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
